interface HexagonProgressProps {
  percentage: number;
  size: number;
  strokeWidth: number;
  progressColor: string;
  backgroundColor?: string;
  textColor?: string;
  status: string;
  insuranceDelivery?: number;
  centerFill?: string;
}

const HexagonProgress = ({
  percentage = 0,
  size = 128,
  status = "inProgress",
  strokeWidth = 20,
  progressColor = "#004781",
  backgroundColor = "#D9D9DEFF",
  textColor = "#374151",
  insuranceDelivery = 0,
  centerFill,
}: HexagonProgressProps) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const radiusX = size / 2 - strokeWidth / 2; // Horizontal radius
  const radiusY = size / 2 - strokeWidth / 2; // Vertical radius (smaller for elongation)

  // Calculate hexagon points with flat top/bottom
  const points: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i; // Flat top/bottom orientation
    const x = centerX + radiusX * Math.cos(angle);
    const y = centerY + radiusY * Math.sin(angle);
    points.push([x, y]);
  }

  // Calculate midpoint of top edge (between top-left and top-right vertices)
  // For flat-top hexagon: points[4] is top-left, points[5] is top-right
  const topEdgeMidpoint: [number, number] = [
    (points[4][0] + points[5][0]) / 2,
    (points[4][1] + points[5][1]) / 2,
  ];

  // Create path starting from top edge midpoint, going clockwise through vertices
  const hexPath = `M ${topEdgeMidpoint.join(",")} L ${points[5].join(",")} L ${points[0].join(
    ",",
  )} L ${points[1].join(",")} L ${points[2].join(",")} L ${points[3].join(",")} L ${points[4].join(
    ",",
  )} Z`;

  // Inner fill hexagon — inset by half strokeWidth so it sits inside the stroke ring
  const innerR = size / 2 - strokeWidth;
  const innerPoints: [number, number][] = Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 3) * i;
    return [centerX + innerR * Math.cos(angle), centerY + innerR * Math.sin(angle)];
  });
  const innerHexPath = `M ${innerPoints[0].join(",")} ${innerPoints
    .slice(1)
    .map((p) => `L ${p.join(",")}`)
    .join(" ")} Z`;

  return status === "completed" ? (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
      {centerFill && <path d={innerHexPath} fill={centerFill} stroke="none" />}
      {/* Completed hexagon with fill */}
      <path
        d={hexPath}
        fill={progressColor}
        stroke={progressColor}
        strokeWidth="8"
        // strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Checkmark icon */}
      <path
        d={`M ${centerX - size * 0.15},${centerY + size * 0.05}
         L ${centerX - size * 0.03},${centerY + size * 0.18}
         L ${centerX + size * 0.18},${centerY - size * 0.12}`}
        stroke="white"
        strokeWidth={size > 100 ? "8" : "2"}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ) : (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="transform transition-all duration-300"
    >
      {/* Define black and white diagonal stripe pattern - scaled to stroke width */}
      <defs>
        {(() => {
          // Denser stripes with angle matching hexagon edge (60 degrees)
          const blackStripeWidth = Math.max(strokeWidth * 0.12, 0.8);
          const whiteStripeWidth = Math.max(strokeWidth * 0.15, 1.2);
          const patternSize = blackStripeWidth + whiteStripeWidth;
          // Offset to ensure black stripe appears first from left
          const offset = whiteStripeWidth + 5;
          return (
            <pattern
              id={`bwStripes-${size}`}
              patternUnits="userSpaceOnUse"
              width={patternSize}
              height={patternSize}
              patternTransform={`rotate(-35) translate(-${offset}, 0)`}
            >
              <rect width={blackStripeWidth} height={patternSize} fill="#1A1A1A" />
              <rect
                x={blackStripeWidth}
                width={whiteStripeWidth}
                height={patternSize}
                fill="#E8E8E8"
              />
            </pattern>
          );
        })()}
      </defs>
      {/* Calculate actual path length */}
      {(() => {
        const edges = [
          [topEdgeMidpoint, points[5]],
          [points[5], points[0]],
          [points[0], points[1]],
          [points[1], points[2]],
          [points[2], points[3]],
          [points[3], points[4]],
          [points[4], topEdgeMidpoint],
        ];
        const actualPathLength = edges.reduce(
          (sum, [p1, p2]) => sum + Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2),
          0,
        );

        // Calculate cumulative lengths at each vertex to detect when progress is near them
        const vertexPositions: number[] = [];
        let cumulativeLength = 0;
        // eslint-disable-next-line no-restricted-syntax
        for (const [p1, p2] of edges) {
          const edgeLength = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
          cumulativeLength += edgeLength;
          vertexPositions.push(cumulativeLength);
        }

        // Calculate base progress length
        // Scale the percentage to leave a visible gap at high values (95% max visible progress)
        // This ensures that even at 98-99%, there's still a gray gap visible at the top
        const maxVisibleProgress = 0.97; // Cap at 95% of path to always show some gray
        const scaledPercentage = Math.min(percentage / 100, maxVisibleProgress);
        let progressLength = scaledPercentage * actualPathLength;

        if (percentage > 10) {
          // console.log("percentage", percentage);
          // Check if progress is near any vertex and add minimal buffer to push through
          const vertexThreshold = strokeWidth * 0.4; // Distance to detect "near vertex"
          const vertexBuffer = strokeWidth * 0.4; // Minimal buffer to push through vertex
          // Find the nearest vertex that progress is approaching
          const nearVertex = vertexPositions.find((vertexPos) => {
            const distanceToVertex = Math.abs(progressLength - vertexPos);
            return distanceToVertex < vertexThreshold && distanceToVertex > 0;
          });

          // If near a vertex, add buffer to push through and hide crooked edge
          // But ensure we don't exceed the max visible progress limit
          if (nearVertex !== undefined) {
            progressLength = Math.min(
              nearVertex + vertexBuffer,
              actualPathLength * maxVisibleProgress,
            );
          }
        } else if (percentage === 10) {
          // At 10%, progress touches the top-right corner with slanting end
          const firstVertexPos = vertexPositions[0]; // Position of point 5 (top-right corner)
          progressLength = firstVertexPos + strokeWidth * 0.7;
        } else {
          progressLength =
            size > 100 && percentage > 6
              ? scaledPercentage * actualPathLength - 10
              : scaledPercentage * actualPathLength;
        }

        // Use minimal offset for fine-tuning
        const progressOffset = 0;
        // const insuranceOffset = 0;

        return (
          <>
            {centerFill && <path d={innerHexPath} fill={centerFill} stroke="none" />}
            {/* Background hexagon */}
            <path
              d={hexPath}
              fill="none"
              stroke={backgroundColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Progress stroke - only render if percentage > 0 and not 10% */}
            {percentage > 0 && percentage !== 10 && (
              <path
                d={hexPath}
                fill="none"
                stroke={progressColor}
                strokeWidth={strokeWidth}
                strokeLinecap="butt"
                strokeLinejoin="miter"
                strokeDasharray={`${progressLength + progressOffset} ${actualPathLength}`}
                style={{
                  transition:
                    "stroke-dasharray 0.5s ease-in-out, stroke-dashoffset 0.5s ease-in-out",
                }}
              />
            )}

            {/* 10% progress - filled polygon mirroring the insurance delivery */}
            {percentage === 10 && (
              <>
                {(() => {
                  const halfStroke = strokeWidth / 2;

                  // Vertex 5 (top-right corner) - smooth corner like light blue
                  const v5Dx = points[5][0] - centerX;
                  const v5Dy = points[5][1] - centerY;
                  const v5Len = Math.hypot(v5Dx, v5Dy);
                  const cornerOuter: [number, number] = [
                    points[5][0] + (v5Dx / v5Len) * halfStroke * 1.1,
                    points[5][1] + (v5Dy / v5Len) * halfStroke * 1.1,
                  ];
                  const cornerInner: [number, number] = [
                    points[5][0] - (v5Dx / v5Len) * halfStroke * 1.1,
                    points[5][1] - (v5Dy / v5Len) * halfStroke * 1.1,
                  ];

                  // Top edge: outer and inner points at midpoint
                  const topOuter: [number, number] = [
                    topEdgeMidpoint[0],
                    topEdgeMidpoint[1] - halfStroke,
                  ];
                  const topInner: [number, number] = [
                    topEdgeMidpoint[0],
                    topEdgeMidpoint[1] + halfStroke,
                  ];

                  // Simple polygon from midpoint to corner (no extension past corner)
                  const shapePath = `M ${topOuter.join(",")} 
                    L ${cornerOuter.join(",")} 
                    L ${cornerInner.join(",")} 
                    L ${topInner.join(",")} Z`;

                  return <path d={shapePath} fill={progressColor} stroke="none" />;
                })()}
              </>
            )}

            {/* Insurance delivery - fills top-left corner with diagonal stripes */}
            {insuranceDelivery > 0 && (
              <>
                {(() => {
                  const halfStroke = strokeWidth / 2;

                  // Left edge direction and normal
                  const leftDx = points[3][0] - points[4][0];
                  const leftDy = points[3][1] - points[4][1];
                  const leftLen = Math.hypot(leftDx, leftDy);
                  const leftNx = -leftDy / leftLen;
                  const leftNy = leftDx / leftLen;

                  // Vertex 4 (top-left corner) - extend outward more to cover gray
                  const v4Dx = points[4][0] - centerX;
                  const v4Dy = points[4][1] - centerY;
                  const v4Len = Math.hypot(v4Dx, v4Dy);
                  const cornerOuter: [number, number] = [
                    points[4][0] + (v4Dx / v4Len) * halfStroke * 1.1,
                    points[4][1] + (v4Dy / v4Len) * halfStroke * 1.1,
                  ];
                  const cornerInner: [number, number] = [
                    points[4][0] - (v4Dx / v4Len) * halfStroke * 1.1,
                    points[4][1] - (v4Dy / v4Len) * halfStroke * 1.1,
                  ];

                  // Top edge: outer and inner points at midpoint
                  const topOuter: [number, number] = [
                    topEdgeMidpoint[0],
                    topEdgeMidpoint[1] - halfStroke,
                  ];
                  const topInner: [number, number] = [
                    topEdgeMidpoint[0],
                    topEdgeMidpoint[1] + halfStroke,
                  ];

                  // End at top-left corner with diagonal cut (just reaching the edge)
                  const slant = strokeWidth * 0.2;
                  // End position just at the corner (very small offset down the left edge)
                  const endPos = 0.01;
                  const endPoint: [number, number] = [
                    points[4][0] + leftDx * endPos,
                    points[4][1] + leftDy * endPos,
                  ];
                  // Outer end (slanted back towards corner)
                  const endOuter: [number, number] = [
                    endPoint[0] + leftNx * halfStroke - (leftDx / leftLen) * slant,
                    endPoint[1] + leftNy * halfStroke - (leftDy / leftLen) * slant,
                  ];
                  // Inner end (slanted forward)
                  const endInner: [number, number] = [
                    endPoint[0] - leftNx * halfStroke + (leftDx / leftLen) * slant,
                    endPoint[1] - leftNy * halfStroke + (leftDy / leftLen) * slant,
                  ];

                  // Single continuous polygon filling the corner completely
                  const shapePath = `M ${topOuter.join(",")} 
                    L ${cornerOuter.join(",")} 
                    L ${endOuter.join(",")} 
                    L ${endInner.join(",")} 
                    L ${cornerInner.join(",")} 
                    L ${topInner.join(",")} Z`;

                  return <path d={shapePath} fill={`url(#bwStripes-${size})`} stroke="none" />;
                })()}
              </>
            )}

            {/* Percentage text */}
            <text
              x={centerX}
              y={centerY}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={textColor}
              fontSize={size * 0.13}
              fontWeight="700"
              className="conthrax-font dark:fill-gray-300"
            >
              {Math.floor(percentage)}%
            </text>
          </>
        );
      })()}
    </svg>
  );
};

export default HexagonProgress;
